// @ts-strict-ignore
import { ArticleUtils } from '@article/util/article-utils';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { EventDispatcher, Vector3 } from 'three';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';

export class ArticleNode extends EventDispatcher<Record<string, {}>> {
  public id: string;
  private _parent: ArticleNode;
  public children: ArticleNode[] = null;

  private _article: ArticleVO;
  public articleUsage?: ArticleUsageVO;
  public isDeletable: boolean;
  public isComposite: boolean;
  public partSelectionAllowed: boolean;
  public drillingHoleUsageIds: string[] = [];
  public drillingHoleUsages: ArticleUsageVO[] = [];
  public positionLabel: string = '';
  public isArticleListNode: boolean = false;
  public articleListArticleUsageIds: string[] = [];
  public articleTreeLabel: string = '';
  public articleTreeDataTip: string = '';

  private _articleContainer: ArticleContainer = null;

  constructor(
    private containerService: ContainerService,
    nodeId: string = null,
    parentNode: ArticleNode = null,
    article: ArticleVO = null,
    articleUsage: ArticleUsageVO = null,
    isDeletable: boolean = false,
    isComposite: boolean = false,
    partSelectionAllowed: boolean = false
  ) {
    super();
    this.id = nodeId;
    this.parent = parentNode;
    this.article = article;
    this.articleUsage = articleUsage;
    this.isDeletable = isDeletable;
    this.isComposite = isComposite;
    this.partSelectionAllowed = partSelectionAllowed;
  }

  public set article(value: ArticleVO) {
    this._article = value;
    this.dispatchEvent({ type: 'articleChanged' });
  }

  public get article(): ArticleVO {
    return this._article;
  }

  public get parent(): ArticleNode {
    return this._parent;
  }

  public set parent(value: ArticleNode) {
    this._parent = value;
  }

  public get articleUsageId(): string {
    if (!this.articleUsage) {
      return null;
    }
    return this.articleUsage.id;
  }

  public get effectiveArticleUsageId(): string {
    // in composite article: nodes should check node id (node id = usage id for whole composite article)
    // rather than usage id of separate articles
    return this.isComposite ? this.id : this.articleUsageId;
  }

  // [Bindable(event = "articleChanged")]
  public get articleNo(): string {
    if (!this.article) {
      return null;
    }
    return this.article.articleNo;
  }

  public get articleOrCustomArticleNo(): string {
    if (!this.article) {
      return null;
    }

    return ArticleUtils.isCustomArticle(this.article) ?
      CustomArticleUtils.getCustomArticleNumber(this.articleUsage!)
      : this.article.articleNo;
  }

  public get parentId(): string {
    if (!this.parent) {
      return null;
    }
    return this.parent.id;
  }

  public get distanceToRoot(): number {
    const root: ArticleNode = this.getRoot();
    const rootArticleContainer: ArticleContainer = root.articleContainer;
    let result: number = NaN;
    if (rootArticleContainer) {
      const thisArticleContainer: ArticleContainer = this.articleContainer;
      if (thisArticleContainer?.bounds) {
        const rootArticleContainerScenePosition: Vector3 = rootArticleContainer.getWorldPosition(new Vector3());
        const thisArticleContainerScenePosition: Vector3 = thisArticleContainer.localToWorld(thisArticleContainer.bounds.getCenter(new Vector3()));
        result = Math.abs(thisArticleContainerScenePosition.x - rootArticleContainerScenePosition.x);
      }
    }
    return result;
  }

  public clone(): ArticleNode {
    const instance: ArticleNode = new ArticleNode(
      this.containerService,
      this.id,
      this.parent,
      this.article,
      this.articleUsage,
      this.isDeletable,
      this.isComposite,
      this.partSelectionAllowed
    );
    instance.children = this.cloneChildren();
    instance.positionLabel = this.positionLabel;
    if (!this.drillingHoleUsageIds) {
      instance.drillingHoleUsageIds = null;
    } else {
      instance.drillingHoleUsageIds = this.drillingHoleUsageIds.slice(0);
    }
    if (!this.drillingHoleUsages) {
      instance.drillingHoleUsages = null;
    } else {
      instance.drillingHoleUsages = this.drillingHoleUsages.slice(0);
    }
    return instance;
  }

  private cloneChildren(): ArticleNode[] {
    if (!this.children) {
      return this.children;
    }
    const collection: ArticleNode[] = [];
    for (const child of this.children) {
      collection.push(child.clone());
    }
    return collection;
  }

  public resetChildren(newChild: ArticleNode = null): void {
    if (!newChild) {
      this.children = null;
    } else {
      this.children = [newChild];
    }
  }

  private get articleContainer(): ArticleContainer {
    if (this._articleContainer) {
      return this._articleContainer;
    }
    this._articleContainer = this.containerService.findArticleContainerInGlobal(this.articleUsageId);
    return this._articleContainer;
  }

  private getRoot(): ArticleNode {
    if (!this.parent) {
      return this;
    } else {
      return this.parent.getRoot();
    }
  }

  public findFirstNodeInTreeByArticleId(articleId: number): ArticleNode {
    if (this.article && this.article.identity === articleId) {
      return this;
    }
    if (!this.children || this.children.length === 0) {
      return null;
    }
    for (const child of this.children) {
      if (child) {
        const subChildArticleNode: ArticleNode = child.findFirstNodeInTreeByArticleId(articleId);
        if (subChildArticleNode) {
          return subChildArticleNode;
        }
      }
    }
    return null;
  }

  public findFirstNodeInTreeByArticleUsageId(articleUsageId: string): ArticleNode {
    if (this.articleUsageId === articleUsageId) {
      return this;
    }
    if (this.articleListArticleUsageIds) {
      const usageIdFound: boolean = this.articleListArticleUsageIds.indexOf(articleUsageId) > -1;
      if (usageIdFound) {
        return this;
      }
    }
    if (!this.children || this.children.length === 0) {
      return null;
    }
    for (const child of this.children) {
      if (child) {
        const subChildArticleNode: ArticleNode = child.findFirstNodeInTreeByArticleUsageId(articleUsageId);
        if (subChildArticleNode) {
          return subChildArticleNode;
        }
      }
    }
    return null;
  }

  public getFirstDrillingHoleUsage(): ArticleUsageVO {
    if (!this.drillingHoleUsages || this.drillingHoleUsages.length === 0) {
      return null;
    }
    return this.drillingHoleUsages[0] as ArticleUsageVO;
  }

  public findDrillingHoleUsageByUsageId(drillingHoleUsageId: string): ArticleUsageVO {
    if (!this.drillingHoleUsages || this.drillingHoleUsages.length === 0) {
      return null;
    }
    for (const drillingHoleUsage of this.drillingHoleUsages) {
      if (drillingHoleUsage.id === drillingHoleUsageId) {
        return drillingHoleUsage;
      }
    }
    return null;
  }
}
